import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from 'next-themes'
import { supabase } from '@/integrations/supabase/client'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard, Building2, Users, ShieldCheck, CreditCard,
  Ticket, Share2, ScrollText, Settings, Eye, LogOut,
  Activity, BarChart3, Bell, MessageCircle, ToggleLeft, GitCommit,
  Menu, X, Sun, Moon, Briefcase, Network, Rss,
} from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { OverviewView } from '@/components/Admin/OverviewView'
import { InstitutionsView } from '@/components/Admin/InstitutionsView'
import { AllUsersView } from '@/components/Admin/AllUsersView'
import { PlatformRolesView } from '@/components/Admin/PlatformRolesView'
import { BillingView } from '@/components/Admin/BillingView'
import { SupportInboxView } from '@/components/Admin/SupportInboxView'
import { ConorbitSocialView } from '@/components/Admin/ConorbitSocialView'
import { ConorbitTeamView } from '@/components/Admin/ConorbitTeamView'
import { PlatformSettingsView } from '@/components/Admin/PlatformSettingsView'
import { AuditLogView } from '@/components/Admin/AuditLogView'
import { AdminProfileForm } from '@/components/Admin/AdminProfileForm'
import { SystemHealthView } from '@/components/Admin/SystemHealthView'
import { PlatformAnalyticsView } from '@/components/Admin/PlatformAnalyticsView'
import { NotificationsView } from '@/components/Admin/NotificationsView'
import { LiveChatView } from '@/components/Admin/LiveChatView'
import { FeatureFlagsView } from '@/components/Admin/FeatureFlagsView'
import { ChangelogView } from '@/components/Admin/ChangelogView'
import { LeadsView } from '@/components/Admin/LeadsView'
import { AssociationsView } from '@/components/Admin/AssociationsView'

interface NavItem {
  id: string
  label: string
  Icon: React.ComponentType<{ className?: string }>
}

const PLATFORM_NAV: Record<string, NavItem[]> = {
  super_admin: [
    { id: 'overview',            label: 'Overview',            Icon: LayoutDashboard },
    { id: 'conorbit-social',     label: 'Social Hub',          Icon: Rss },
    { id: 'organisations',       label: 'Institutions',        Icon: Building2 },
    { id: 'associations',        label: 'Associations',        Icon: Network },
    { id: 'leads',               label: 'Enterprise Leads',    Icon: Briefcase },
    { id: 'users',               label: 'All Users',           Icon: Users },
    { id: 'platform-roles',      label: 'Platform Roles',      Icon: ShieldCheck },
    { id: 'platform-analytics',  label: 'Analytics',           Icon: BarChart3 },
    { id: 'system-health',       label: 'System Health',       Icon: Activity },
    { id: 'notifications',       label: 'Notifications',       Icon: Bell },
    { id: 'support-inbox',       label: 'Support Inbox',       Icon: Ticket },
    { id: 'live-chat',           label: 'Live Chat',           Icon: MessageCircle },
    { id: 'billing',             label: 'Billing',             Icon: CreditCard },
    { id: 'feature-flags',       label: 'Feature Flags',       Icon: ToggleLeft },
    { id: 'changelog',           label: 'Changelog',           Icon: GitCommit },
    { id: 'conorbit-team',       label: 'CONORBIT Team',       Icon: Users },
    { id: 'audit-log',           label: 'Audit Log',           Icon: ScrollText },
    { id: 'platform-settings',   label: 'Platform Settings',   Icon: Settings },
  ],
  staff: [
    { id: 'overview',            label: 'Overview',            Icon: LayoutDashboard },
    { id: 'conorbit-social',     label: 'Social Hub',          Icon: Rss },
    { id: 'organisations',       label: 'Institutions',        Icon: Building2 },
    { id: 'platform-analytics',  label: 'Analytics',           Icon: BarChart3 },
    { id: 'notifications',       label: 'Notifications',       Icon: Bell },
    { id: 'support-inbox',       label: 'Support Inbox',       Icon: Ticket },
    { id: 'live-chat',           label: 'Live Chat',           Icon: MessageCircle },
    { id: 'changelog',           label: 'Changelog',           Icon: GitCommit },
  ],
  support_agent: [
    { id: 'support-inbox',       label: 'My Tickets',          Icon: Ticket },
    { id: 'live-chat',           label: 'Live Chat',           Icon: MessageCircle },
    { id: 'support-session',     label: 'Session Status',      Icon: Eye },
  ],
  billing_manager: [
    { id: 'billing',             label: 'Billing',             Icon: CreditCard },
    { id: 'notifications',       label: 'Notifications',       Icon: Bell },
  ],
}

const ROLE_LABELS: Record<string, string> = {
  super_admin:     'Platform Administrator',
  staff:           'CONORBIT Staff',
  support_agent:   'Support Agent',
  billing_manager: 'Billing Manager',
}

export default function AdminShell() {
  const [activeView, setActiveView] = useState<string>('overview')
  const [profileOpen, setProfileOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const isDark = mounted && (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches))

  const { data: adminProfile } = useQuery({
    queryKey: ['admin-identity'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('app_admins' as any)
        .select('first_name, last_name, email, platform_role')
        .eq('user_id', user.id)
        .single() as any)
      return data as {
        first_name: string
        last_name: string
        email: string
        platform_role: string
      } | null
    }
  })

  const platformRole = adminProfile?.platform_role ?? 'staff'
  const navItems = PLATFORM_NAV[platformRole] ?? PLATFORM_NAV.staff

  const { data: unreadCount } = useQuery({
    queryKey: ['platform-notifications-unread-count'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return 0
      const { data } = await supabase
        .from('platform_notifications')
        .select('id, read_by')
        .order('created_at', { ascending: false })
        .limit(100)
      return (data ?? []).filter(n => !(n.read_by ?? []).includes(user.id)).length
    },
    refetchInterval: 30000,
  })

  const initials = adminProfile
    ? `${adminProfile.first_name?.[0] ?? ''}${adminProfile.last_name?.[0] ?? ''}`
    : '?'

  const fullName = adminProfile
    ? `${adminProfile.last_name ?? ''} ${adminProfile.first_name ?? ''}`.trim()
    : '…'

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/admin/login'
  }

  const renderView = () => {
    switch (activeView) {
      case 'overview':            return <OverviewView onNavigate={setActiveView} />
      case 'organisations':       return <InstitutionsView />
      case 'associations':        return <AssociationsView />
      case 'leads':               return <LeadsView />
      case 'users':               return <AllUsersView platformRole={platformRole} />
      case 'platform-roles':      return <PlatformRolesView />
      case 'platform-analytics':  return <PlatformAnalyticsView />
      case 'system-health':       return <SystemHealthView />
      case 'notifications':       return <NotificationsView />
      case 'billing':             return <BillingView />
      case 'support-inbox':       return <SupportInboxView platformRole={platformRole} />
      case 'support-session':     return <SupportInboxView platformRole={platformRole} sessionOnly />
      case 'live-chat':           return <LiveChatView platformRole={platformRole} />
      case 'feature-flags':       return <FeatureFlagsView />
      case 'changelog':           return <ChangelogView platformRole={platformRole} />
      case 'conorbit-social':     return <ConorbitSocialView />
      case 'conorbit-team':      return <ConorbitTeamView />
      case 'audit-log':           return <AuditLogView />
      case 'platform-settings':   return <PlatformSettingsView />
      default:                    return <OverviewView onNavigate={setActiveView} />
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Admin sidebar */}
      <aside className={cn(
        'fixed lg:static inset-y-0 left-0 z-30 w-56 bg-[#0D2240] flex flex-col',
        'transition-transform duration-200 ease-in-out lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Close button, mobile only */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-3 right-3 p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
        >
          <X size={16} />
        </button>

        {/* CONORBIT product wordmark, hardcoded, never from DB */}
        <div className="p-4 border-b border-white/10">
          <img src="/conorbit-logo.png" alt="CONORBIT" className="h-8 rounded" />
          <div className="text-[#4A6A88] text-[11px] mt-1.5">Platform Administration</div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveView(id)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative',
                activeView === id
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
              {id === 'notifications' && (unreadCount ?? 0) > 0 && (
                <span className="ml-auto w-2 h-2 rounded-full bg-red-500 shrink-0" />
              )}
            </button>
          ))}
        </nav>

        {/* Bottom user pill, PERSON name from app_admins, never product name */}
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setProfileOpen(true)}
            className="w-full flex items-center gap-2.5 px-2 py-1.5 mb-1 rounded-lg hover:bg-white/5 transition-all text-left"
          >
            <div className="w-8 h-8 rounded-full bg-[#C8A84B] flex items-center justify-center text-[#0D2240] text-xs font-bold shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">{fullName}</div>
              <div className="text-[10px] truncate" style={{ color: '#C8A84B' }}>
                CONORBIT · {ROLE_LABELS[platformRole] ?? platformRole}
              </div>
            </div>
          </button>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
          <button
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
          >
            {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {isDark ? 'Light mode' : 'Dark mode'}
          </button>
        </div>
      </aside>

      {/* Profile sheet */}
      <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
        <SheetContent side="right" className="w-[380px]">
          <SheetHeader>
            <SheetTitle>My Profile</SheetTitle>
          </SheetHeader>
          <AdminProfileForm
            adminProfile={adminProfile}
            onClose={() => setProfileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-background border-b border-border">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-muted">
            <Menu size={18} className="text-foreground" />
          </button>
          <span className="text-[14px] font-bold text-[#C8A84B] tracking-widest uppercase">
            CONORBIT
          </span>
        </div>
        <div className="p-8">
          {renderView()}
        </div>
      </main>
    </div>
  )
}

